// Settings — full redesign matching TIMPLE.html reference.
// Layout: left sidebar (desktop) + content, synced with ?filter= subnav param.

import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { I } from "../../shared/Icon";
import { useAuth } from "../../shared/stores/auth";
import { useTheme } from "../../shared/stores/theme";
import { toast } from "../../shared/stores/toasts";

// ─── Types ────────────────────────────────────────────────────────────────────
type Prefs = {
  language: string;
  timezone: string;
  date_format: string;
  week_start: number;
  work_schedule: string | null;
  mail_signature: string | null;
  shortcuts: string | null;
  rail_order: string | null;
  rail_sections: string | null;
};

// ─── Sections definition ──────────────────────────────────────────────────────
const SECTIONS = [
  { id: "profile",  label: "프로필",      icon: "User",     desc: "개인 정보 및 사진" },
  { id: "security", label: "계정 / 보안", icon: "Lock",     desc: "비밀번호, MFA, 세션" },
  { id: "notif",    label: "알림",        icon: "Bell",     desc: "채널별 알림 설정" },
  { id: "theme",    label: "테마 / 외관", icon: "Sun",      desc: "다크모드, 색상, 폰트, 밀도" },
  { id: "lang",     label: "언어 / 지역", icon: "Globe",    desc: "표시 언어, 시간대, 형식" },
  { id: "work",     label: "근무 설정",   icon: "Clock",    desc: "근무 시간, 자동 출퇴근" },
  { id: "sig",      label: "메일 서명",   icon: "FileText", desc: "이메일 서명 및 답장 양식" },
  { id: "keys",     label: "단축키",      icon: "Settings", desc: "키보드 단축키 관리" },
  { id: "data",     label: "데이터",      icon: "Folder",   desc: "내보내기, 가져오기, 초기화" },
] as const;

type SectionId = typeof SECTIONS[number]["id"];

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function SettingsPage() {
  const [params, setParams] = useSearchParams();
  const sec = (params.get("filter") || "profile") as SectionId;
  const setSec = (id: string) => setParams({ filter: id });

  return (
    <div style={{ display: "flex", height: "100%", minHeight: 0, background: "var(--bg)" }}>
      {/* Left sidebar — desktop only */}
      <aside style={{
        width: 240, flexShrink: 0,
        background: "var(--surface)", borderRight: "1px solid var(--border)",
        padding: "16px 12px", overflowY: "auto",
        display: "flex", flexDirection: "column",
      }}>
        {SECTIONS.map((s) => {
          const Ic = (I as Record<string, (p: { size: number }) => JSX.Element>)[s.icon] ?? I.Settings;
          const active = sec === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setSec(s.id)}
              style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                width: "100%", padding: "10px 12px", marginBottom: 2,
                border: "none", borderRadius: 8,
                background: active ? "var(--p-tint)" : "transparent",
                color: active ? "var(--p-700)" : "var(--text-2)",
                textAlign: "left", cursor: "pointer", fontFamily: "inherit",
              }}
            >
              <Ic size={16} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: active ? 600 : 500 }}>{s.label}</div>
                <div style={{ fontSize: 11, color: active ? "var(--p-600)" : "var(--text-3)", marginTop: 1 }}>{s.desc}</div>
              </div>
            </button>
          );
        })}
      </aside>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, overflowY: "auto", padding: "28px 32px" }}>
        <div style={{ maxWidth: 720 }}>
          {sec === "profile"  && <SectionProfile />}
          {sec === "security" && <SectionAccount />}
          {sec === "notif"    && <SectionNotif />}
          {sec === "theme"    && <SectionLook />}
          {sec === "lang"     && <SectionLang />}
          {sec === "work"     && <SectionWork />}
          {sec === "sig"      && <SectionSign />}
          {sec === "keys"     && <SectionKeys />}
          {sec === "data"     && <SectionData />}
        </div>
      </div>
    </div>
  );
}

// ─── Shared building blocks ───────────────────────────────────────────────────
function Card({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, letterSpacing: "-0.005em" }}>{title}</h2>
      {desc
        ? <p style={{ margin: "0 0 14px", fontSize: 12.5, color: "var(--text-3)" }}>{desc}</p>
        : <div style={{ height: 14 }} />}
      <div className="t-card" style={{ padding: 0, overflow: "hidden" }}>
        {children}
      </div>
    </div>
  );
}

function Row({
  label, hint, children, bordered = true,
}: { label: React.ReactNode; hint?: string; children?: React.ReactNode; bordered?: boolean }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", padding: "14px 18px",
      borderTop: bordered ? "1px solid var(--border)" : "none",
      gap: 16,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 500 }}>{label}</div>
        {hint && <div style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 3, lineHeight: 1.5 }}>{hint}</div>}
      </div>
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 8 }}>{children}</div>
    </div>
  );
}

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!value)}
      style={{
        width: 38, height: 22,
        background: value ? "var(--p-600)" : "var(--border-strong)",
        border: "none", borderRadius: 11, padding: 0,
        position: "relative", cursor: "pointer", transition: "background .15s",
      }}
    >
      <span style={{
        position: "absolute", top: 2, left: value ? 18 : 2,
        width: 18, height: 18, borderRadius: 9,
        background: "white", boxShadow: "0 1px 3px rgba(0,0,0,.2)",
        transition: "left .15s",
      }} />
    </button>
  );
}

function Sel({
  value, options, onChange, width = 160,
}: {
  value: string;
  options: (string | { value: string; label: string })[];
  onChange: (v: string) => void;
  width?: number;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width, height: 32, padding: "0 28px 0 10px",
        border: "1px solid var(--border-strong)", borderRadius: 6,
        background: "var(--surface)", color: "var(--text)",
        fontFamily: "inherit", fontSize: 13, outline: "none",
        appearance: "none", cursor: "pointer",
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='%236b7280' stroke-width='1.6' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 10px center",
      }}
    >
      {options.map((o) =>
        typeof o === "string"
          ? <option key={o} value={o}>{o}</option>
          : <option key={o.value} value={o.value}>{o.label}</option>
      )}
    </select>
  );
}

function Inp({
  value, onChange, type = "text", placeholder, width, mono,
}: {
  value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; width?: number; mono?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: width || 220, height: 32, padding: "0 10px",
        border: "1px solid var(--border-strong)", borderRadius: 6,
        background: "var(--surface)", color: "var(--text)",
        fontFamily: mono ? "JetBrains Mono, monospace" : "inherit",
        fontSize: 13, outline: "none",
      }}
    />
  );
}

function SaveBar({ onCancel, onSave, saving }: { onCancel: () => void; onSave: () => void; saving: boolean }) {
  return (
    <div style={{
      position: "sticky", bottom: 0, marginTop: 8,
      background: "var(--surface)", borderTop: "1px solid var(--border)",
      padding: "12px 0", display: "flex", alignItems: "center", gap: 12,
    }}>
      <span style={{ fontSize: 12.5, color: "var(--text-3)" }}>저장하지 않은 변경 사항이 있습니다.</span>
      <div style={{ flex: 1 }} />
      <button className="t-btn" onClick={onCancel}>되돌리기</button>
      <button className="t-btn t-btn-primary" onClick={onSave} disabled={saving}>
        {saving ? "저장 중…" : "변경 저장"}
      </button>
    </div>
  );
}

function usePrefs() {
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  useEffect(() => {
    api.get<Prefs>("/users/me/preferences").then((r) => setPrefs(r.data)).catch(() => {});
  }, []);
  const save = async (patch: Partial<Prefs>) => {
    try {
      const r = await api.put<Prefs>("/users/me/preferences", patch);
      setPrefs(r.data);
      toast.success("저장됨");
    } catch (e: any) {
      toast.error("저장 실패", e?.response?.data?.error?.message ?? "");
    }
  };
  return { prefs, save };
}

// ─── 1. Profile ───────────────────────────────────────────────────────────────
function SectionProfile() {
  const user = useAuth((s) => s.user);
  const [name, setName]     = useState(user?.name ?? "");
  const [nameEn, setNameEn] = useState("");
  const [phone, setPhone]   = useState("");
  const [mobile, setMobile] = useState("");
  const [title, setTitle]   = useState("");
  const [intro, setIntro]   = useState("");
  const [dirty, setDirty]   = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef             = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.get<any>("/users/me").then((r) => {
      const d = r.data;
      setName(d.name ?? "");
      setNameEn(d.name_en ?? "");
      setPhone(d.phone ?? "");
      setMobile(d.mobile ?? "");
    }).catch(() => {});
  }, []);

  const mark = () => setDirty(true);
  const reset = () => {
    setName(user?.name ?? "");
    setNameEn(""); setPhone(""); setMobile(""); setTitle(""); setIntro("");
    setDirty(false);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me", { name, name_en: nameEn, phone, mobile });
      toast.success("프로필이 저장되었습니다");
      setDirty(false);
    } catch (e: any) {
      toast.error("저장 실패", e?.response?.data?.error?.message);
    } finally {
      setSaving(false);
    }
  };

  const initials = (name?.slice(0, 2) ?? "?").toUpperCase();

  return (
    <>
      <Card title="프로필" desc="회사 디렉토리나 팀에 표시되는 정보입니다.">
        <div style={{ padding: 20, display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div className="t-av" style={{
              background: "linear-gradient(135deg, var(--p-400), var(--p-600))",
              width: 76, height: 76, fontSize: 28, color: "white",
              borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700,
            }}>{initials}</div>
            <button
              className="t-iconbtn"
              style={{ position: "absolute", bottom: -4, right: -4, width: 26, height: 26, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 13 }}
              onClick={() => fileRef.current?.click()}
            >
              <I.Plus size={13} />
            </button>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const fd = new FormData();
                fd.append("file", file);
                try {
                  toast.info("사진 업로드 중…");
                } catch {}
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 200, fontSize: 12.5, color: "var(--text-3)", lineHeight: 1.65 }}>
            JPG / PNG · 1MB 이하 · 1:1 비율 권장. 회사 디렉토리, 채팅, 결재 카드 등 전 모듈에 사용됩니다.
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              <button className="t-btn t-btn-outline" onClick={() => fileRef.current?.click()}>사진 업로드</button>
            </div>
          </div>
        </div>
        <Row label="이름 (한글)" hint="회사 디렉토리에 표시되는 이름" bordered>
          <Inp value={name} onChange={(v) => { setName(v); mark(); }} width={220} />
        </Row>
        <Row label="이름 (영문)">
          <Inp value={nameEn} onChange={(v) => { setNameEn(v); mark(); }} width={220} />
        </Row>
        <Row label="직책 / 직급">
          <Inp value={title} onChange={(v) => { setTitle(v); mark(); }} width={220} />
        </Row>
        <Row label="연락처 (휴대전화)" hint="긴급 알림 이외에 외부에 노출되지 않습니다.">
          <Inp value={mobile} onChange={(v) => { setMobile(v); mark(); }} mono width={180} />
        </Row>
        <Row label="사무실 전화">
          <Inp value={phone} onChange={(v) => { setPhone(v); mark(); }} mono width={180} />
        </Row>
        <div style={{ padding: 18, borderTop: "1px solid var(--border)" }}>
          <div style={{ fontSize: 13.5, fontWeight: 500, marginBottom: 8 }}>한 줄 소개</div>
          <textarea
            value={intro}
            onChange={(e) => { setIntro(e.target.value); mark(); }}
            maxLength={120}
            style={{
              width: "100%", minHeight: 72, padding: 10, boxSizing: "border-box",
              border: "1px solid var(--border-strong)", borderRadius: 6,
              background: "var(--surface)", color: "var(--text)",
              fontFamily: "inherit", fontSize: 13, outline: "none", resize: "vertical",
            }}
            placeholder="채팅, 결재 문서에 부분 표시됩니다."
          />
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>{intro.length} / 120</div>
        </div>
      </Card>

      <Card title="조직 정보" desc="HR 관리자에 의해 설정됩니다. 변경이 필요하면 인사팀에 요청하세요.">
        <Row label="아이디" bordered={false}>
          <span style={{ fontFamily: "monospace", color: "var(--text-3)", fontSize: 13 }}>
            {user?.username ?? "—"}
          </span>
        </Row>
        <Row label="이메일">
          <span style={{ fontFamily: "monospace", color: "var(--text-3)", fontSize: 13 }}>
            {user?.email ?? "—"}
          </span>
        </Row>
        <Row label="메일 주소">
          <span style={{ fontFamily: "monospace", color: "var(--text-3)", fontSize: 13 }}>
            {user?.username ? `${user.username}@timple.me` : "—"}
          </span>
        </Row>
        <Row label="역할 (시스템 권한)">
          <div style={{ display: "flex", gap: 4 }}>
            {(user?.roles ?? []).map((r) => (
              <span key={r} className="t-pill info" style={{ fontSize: 11 }}>{r}</span>
            ))}
          </div>
        </Row>
      </Card>

      {dirty && <SaveBar onCancel={reset} onSave={save} saving={saving} />}
    </>
  );
}

// ─── 2. Account / Security ────────────────────────────────────────────────────
function SectionAccount() {
  const [mfa, setMfa]               = useState(false);
  const [showPwForm, setShowPwForm] = useState(false);
  const [pw, setPw]                 = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving]     = useState(false);
  const [emailNotify, setEmailNotify] = useState(true);
  const [forceLogout, setForceLogout] = useState(true);
  const user = useAuth((s) => s.user);

  useEffect(() => {
    if (user?.mfa_enabled !== undefined) setMfa(user.mfa_enabled);
  }, [user]);

  const changePassword = async () => {
    if (pw.next !== pw.confirm) { toast.warn("새 비밀번호가 일치하지 않습니다"); return; }
    if (pw.next.length < 8) { toast.warn("비밀번호는 8자 이상이어야 합니다"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/password/change", { current_password: pw.current, new_password: pw.next });
      toast.success("비밀번호가 변경되었습니다. 다시 로그인하세요.");
      setPw({ current: "", next: "", confirm: "" });
      setShowPwForm(false);
    } catch (e: any) {
      toast.error("변경 실패", e?.response?.data?.error?.message);
    } finally {
      setPwSaving(false);
    }
  };

  const setupMfa = async () => {
    try {
      const r = await api.post<{ secret: string; provisioning_uri: string }>("/auth/mfa/setup");
      toast.info("Authenticator 앱으로 QR을 스캔하세요", r.data.provisioning_uri.slice(0, 50) + "…");
    } catch (e: any) {
      toast.error("MFA 설정 실패", e?.response?.data?.error?.message);
    }
  };

  const SESSIONS = [
    { device: "현재 기기 · Chrome", loc: "서울 · 현재 세션", time: "지금", current: true },
    { device: "iPhone · TIMPLE App", loc: "서울 · LTE", time: "30분 전" },
  ];

  return (
    <>
      <Card title="비밀번호" desc="최소 8자 · 대소문자/숫자/특수문자 · 90일마다 변경 권장">
        {!showPwForm ? (
          <Row label="비밀번호" bordered={false} hint="마지막 변경 날짜는 계정 생성 시점으로부터 계산됩니다.">
            <button className="t-btn t-btn-outline" onClick={() => setShowPwForm(true)}>변경</button>
          </Row>
        ) : (
          <div style={{ padding: 18 }}>
            <div style={{ display: "grid", gap: 10, maxWidth: 360 }}>
              <div>
                <label style={{ fontSize: 12, color: "var(--text-3)", display: "block", marginBottom: 4 }}>현재 비밀번호</label>
                <input type="password" className="t-input" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: "var(--text-3)", display: "block", marginBottom: 4 }}>새 비밀번호 (8자 이상)</label>
                <input type="password" className="t-input" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: "var(--text-3)", display: "block", marginBottom: 4 }}>새 비밀번호 확인</label>
                <input type="password" className="t-input" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
              </div>
              {pw.next && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ height: 6, flex: 1, background: "var(--panel)", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{
                      height: "100%", borderRadius: 3, transition: "width .3s",
                      width: pw.next.length < 8 ? "25%" : pw.next.length < 12 ? "55%" : "90%",
                      background: pw.next.length < 8 ? "var(--danger)" : pw.next.length < 12 ? "#F59E0B" : "var(--ok)",
                    }} />
                  </div>
                  <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                    {pw.next.length < 8 ? "약함" : pw.next.length < 12 ? "보통" : "강함"}
                  </span>
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button className="t-btn" onClick={() => { setShowPwForm(false); setPw({ current: "", next: "", confirm: "" }); }}>취소</button>
                <button className="t-btn t-btn-primary" onClick={changePassword} disabled={pwSaving}>
                  {pwSaving ? "변경 중…" : "비밀번호 변경"}
                </button>
              </div>
            </div>
          </div>
        )}
      </Card>

      <Card title="2단계 인증 (MFA)" desc="중요 액션 (결재, 다운로드)에서 추가 인증을 요구합니다.">
        <Row label="MFA 활성화" hint={mfa ? "Authenticator 앱 (Google Authenticator)" : "비활성 — 활성화를 권장합니다."} bordered={false}>
          <Toggle value={mfa} onChange={async (v) => {
            if (v) {
              await setupMfa();
            } else {
              try {
                await api.post("/auth/mfa/disable");
                setMfa(false);
                toast.success("MFA 비활성화됨");
              } catch (e: any) {
                toast.error("실패", e?.response?.data?.error?.message);
              }
            }
          }} />
        </Row>
        {mfa && (
          <Row label="백업 코드 (10개)" hint="긴급 시 사용. 안전한 곳에 보관하세요.">
            <button className="t-btn t-btn-outline" onClick={setupMfa}>코드 재발급</button>
          </Row>
        )}
      </Card>

      <Card title="SSO 연결" desc="외부 ID 공급자와 연결된 계정">
        <Row label="Google Workspace" hint="연결되지 않음" bordered={false}>
          <button className="t-btn t-btn-outline">연결</button>
        </Row>
        <Row label="Microsoft 365" hint="연결되지 않음">
          <button className="t-btn t-btn-outline">연결</button>
        </Row>
      </Card>

      <Card title="활성 세션" desc="다른 기기에서의 로그인을 관리합니다.">
        {SESSIONS.map((s, i) => (
          <Row
            key={i}
            label={
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {s.device}
                {s.current && <span className="t-pill ok" style={{ marginLeft: 4 }}>현재 기기</span>}
              </span>
            }
            hint={`${s.loc} · ${s.time}`}
            bordered={i > 0}
          >
            {!s.current && (
              <button className="t-btn" style={{ color: "var(--danger)" }}>로그아웃</button>
            )}
          </Row>
        ))}
        <div style={{ padding: 14, borderTop: "1px solid var(--border)", background: "var(--panel)" }}>
          <button
            className="t-btn t-btn-outline"
            style={{ color: "var(--danger)", borderColor: "color-mix(in srgb, var(--danger) 30%, transparent)" }}
            onClick={async () => {
              try {
                await api.post("/auth/logout-all");
                toast.success("다른 모든 기기에서 로그아웃됨");
              } catch {}
            }}
          >
            <I.Logout size={13} /> 다른 모든 기기에서 로그아웃
          </button>
        </div>
      </Card>

      <Card title="로그인 알림" desc="새로운 기기에서 로그인 시 알림을 받습니다.">
        <Row label="이메일로 알림" bordered={false}>
          <Toggle value={emailNotify} onChange={setEmailNotify} />
        </Row>
        <Row label="비활성 시 자동 로그아웃" hint="30분 동안 활동이 없으면 자동 종료">
          <Toggle value={forceLogout} onChange={setForceLogout} />
        </Row>
      </Card>
    </>
  );
}

// ─── 3. Notifications ─────────────────────────────────────────────────────────
const NOTIF_KINDS = [
  { id: "approval", label: "결재 요청",    hint: "내가 승인해야 할 결재" },
  { id: "mention",  label: "@멘션",       hint: "채팅, 댓글에서 멘션될 때" },
  { id: "mail",     label: "새 메일",     hint: "받은편지함 신규 메일" },
  { id: "msg",      label: "새 메시지",   hint: "1:1 / 부서 채팅" },
  { id: "cal",      label: "일정 알림",   hint: "15분 / 시작 시간" },
  { id: "deadline", label: "마감일 임박", hint: "프로젝트, 전자결재" },
  { id: "team",     label: "팀 현황",     hint: "휴가, 근태, 공지" },
  { id: "system",   label: "시스템 공지", hint: "유지보수, 백업, 권한 변경" },
] as const;

type NotifKindId = typeof NOTIF_KINDS[number]["id"];

function SectionNotif() {
  type Matrix = Record<NotifKindId, { inapp: boolean; email: boolean; push: boolean }>;
  const [matrix, setMatrix] = useState<Matrix>(() => {
    const init = {} as Matrix;
    NOTIF_KINDS.forEach((k) => {
      init[k.id] = {
        inapp: true,
        email: k.id === "approval" || k.id === "mail" || k.id === "system",
        push:  k.id === "approval" || k.id === "mention" || k.id === "msg" || k.id === "cal" || k.id === "deadline",
      };
    });
    return init;
  });
  const [dnd, setDnd]           = useState(true);
  const [dndStart, setDndStart] = useState("19:00");
  const [dndEnd, setDndEnd]     = useState("09:00");
  const [sound, setSound]       = useState("기본");
  const [digest, setDigest]     = useState("매일 09:00");

  const toggle = (kind: NotifKindId, ch: "inapp" | "email" | "push") =>
    setMatrix((m) => ({ ...m, [kind]: { ...m[kind], [ch]: !m[kind][ch] } }));

  const CHANNELS: { id: "inapp" | "email" | "push"; label: string }[] = [
    { id: "inapp", label: "인앱" },
    { id: "email", label: "이메일" },
    { id: "push",  label: "모바일" },
  ];

  return (
    <>
      <Card title="알림 채널" desc="알림 유형별로 받을 채널을 선택하세요.">
        <div style={{ display: "flex", padding: "10px 18px", fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", background: "var(--panel)" }}>
          <span style={{ flex: 1 }}>알림 유형</span>
          {CHANNELS.map((c) => (
            <span key={c.id} style={{ width: 70, textAlign: "center" }}>{c.label}</span>
          ))}
        </div>
        {NOTIF_KINDS.map((k) => (
          <div key={k.id} style={{ display: "flex", alignItems: "center", padding: "12px 18px", borderTop: "1px solid var(--border)" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 500 }}>{k.label}</div>
              <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>{k.hint}</div>
            </div>
            {CHANNELS.map((c) => (
              <div key={c.id} style={{ width: 70, display: "flex", justifyContent: "center" }}>
                <span
                  onClick={() => toggle(k.id, c.id)}
                  className={`t-check ${matrix[k.id][c.id] ? "checked" : ""}`}
                  style={{ cursor: "pointer" }}
                >
                  {matrix[k.id][c.id] && <I.Check size={11} />}
                </span>
              </div>
            ))}
          </div>
        ))}
      </Card>

      <Card title="방해 금지 시간" desc="이 시간에는 알림 소리와 모바일 팝업이 차단됩니다.">
        <Row label="방해 금지 활성화" bordered={false}>
          <Toggle value={dnd} onChange={setDnd} />
        </Row>
        <Row label="시작 시간">
          <Inp value={dndStart} onChange={setDndStart} width={90} mono />
        </Row>
        <Row label="종료 시간">
          <Inp value={dndEnd} onChange={setDndEnd} width={90} mono />
        </Row>
        <Row label="긴급 (결재 · 시스템) 예외" hint="중요한 알림은 방해 금지에도 받습니다.">
          <Toggle value={true} onChange={() => {}} />
        </Row>
      </Card>

      <Card title="기타">
        <Row label="알림 소리" bordered={false}>
          <Sel value={sound} onChange={setSound} options={["없음", "기본", "팝", "딩", "사이렌"]} />
        </Row>
        <Row label="일일 요약 메일" hint="놓친 알림을 모아서 한 번에 보내드립니다.">
          <Sel value={digest} onChange={setDigest} options={["받지 않음", "매일 09:00", "매일 18:00", "주간 (월요일)"]} />
        </Row>
      </Card>
    </>
  );
}

// ─── 4. Look (Theme / Appearance) ─────────────────────────────────────────────
function SectionLook() {
  const th = useTheme();
  const [theme, setTheme]         = useState<"light" | "dark" | "auto">(th.dark ? "dark" : "light");
  const [reduceMotion, setRM]     = useState(false);

  const applyTheme = (t: "light" | "dark" | "auto") => {
    setTheme(t);
    const dark = t === "dark" || (t === "auto" && window.matchMedia("(prefers-color-scheme: dark)").matches);
    th.set({ dark });
  };

  const THEMES = [
    { id: "light" as const, label: "라이트", bg: "#FFFFFF", border: "#E5E7EB", fg: "#111827" },
    { id: "dark"  as const, label: "다크",   bg: "#111A2E", border: "#2A3656", fg: "#E2E8F0" },
    { id: "auto"  as const, label: "시스템 따름", bg: "linear-gradient(135deg, #FFFFFF 50%, #111A2E 50%)", border: "#B5C0D5", fg: "" },
  ];

  const PRIMARIES = [
    { id: "blue"   as const, color: "#2563EB", label: "디자인 시스템 블루" },
    { id: "purple" as const, color: "#6E6FCF", label: "로고 퍼플" },
  ];

  const DENSITIES = [
    { id: "compact"     as const, label: "Compact", desc: "정보 밀도 최대 · 36px 행" },
    { id: "normal"      as const, label: "Normal",  desc: "표준 · 44px 행" },
    { id: "comfortable" as const, label: "Comfy",   desc: "여유 있는 · 52px 행" },
  ];

  return (
    <>
      <Card title="테마 모드">
        <div style={{ padding: 20, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          {THEMES.map((t) => (
            <button
              key={t.id}
              onClick={() => applyTheme(t.id)}
              style={{
                padding: 0, border: "2px solid",
                borderColor: theme === t.id ? "var(--p-600)" : "var(--border)",
                borderRadius: 10, background: "var(--surface)",
                cursor: "pointer", overflow: "hidden", fontFamily: "inherit",
              }}
            >
              <div style={{ height: 76, background: t.bg, borderBottom: `1px solid ${t.border}`, position: "relative" }}>
                <div style={{ position: "absolute", top: 10, left: 10, width: 18, height: 4, background: t.fg || "#999", opacity: 0.7, borderRadius: 2 }} />
                <div style={{ position: "absolute", top: 18, left: 10, width: 30, height: 3, background: t.fg || "#999", opacity: 0.4, borderRadius: 2 }} />
                <div style={{ position: "absolute", bottom: 10, right: 10, width: 20, height: 20, borderRadius: 4, background: t.id === "light" ? "#2563EB" : t.id === "dark" ? "#6E6FCF" : "linear-gradient(135deg, #2563EB 50%, #6E6FCF 50%)" }} />
              </div>
              <div style={{ padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{t.label}</span>
                {theme === t.id && <I.Check size={14} style={{ color: "var(--p-600)" }} />}
              </div>
            </button>
          ))}
        </div>
      </Card>

      <Card title="강조 색상" desc="버튼, 활성 상태, 차트 메인 컬러에 사용됩니다.">
        <div style={{ padding: 20, display: "flex", gap: 12, flexWrap: "wrap" }}>
          {PRIMARIES.map((c) => (
            <button
              key={c.id}
              onClick={() => th.set({ primary: c.id })}
              style={{
                flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 12,
                padding: 14, border: "2px solid",
                borderColor: th.primary === c.id ? c.color : "var(--border)",
                borderRadius: 10, background: "var(--surface)",
                cursor: "pointer", fontFamily: "inherit", textAlign: "left",
              }}
            >
              <span style={{ width: 36, height: 36, borderRadius: 10, background: c.color, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{c.label}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "monospace" }}>{c.color}</div>
              </div>
              {th.primary === c.id && <I.Check size={16} style={{ color: c.color }} />}
            </button>
          ))}
        </div>
      </Card>

      <Card title="밀도">
        <div style={{ padding: 18, display: "flex", gap: 8 }}>
          {DENSITIES.map((d) => (
            <button
              key={d.id}
              onClick={() => th.set({ density: d.id })}
              style={{
                flex: 1, padding: "12px 14px", border: "2px solid",
                borderColor: th.density === d.id ? "var(--p-600)" : "var(--border)",
                borderRadius: 10, background: th.density === d.id ? "var(--p-tint)" : "var(--surface)",
                cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                color: th.density === d.id ? "var(--p-700)" : "var(--text)",
              }}
            >
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>{d.label}</div>
              <div style={{ fontSize: 11, color: th.density === d.id ? "var(--p-600)" : "var(--text-3)", marginTop: 2 }}>{d.desc}</div>
            </button>
          ))}
        </div>
      </Card>

      <Card title="기타">
        <Row label="폰트" bordered={false}>
          <Sel
            value={th.font}
            onChange={(v) => th.set({ font: v as "pretendard" | "inter" | "system" })}
            width={200}
            options={[
              { value: "pretendard", label: "Pretendard (한글 권장)" },
              { value: "inter",      label: "Inter" },
              { value: "system",     label: "시스템 폰트" },
            ]}
          />
        </Row>
        <Row label="모션 감소" hint="애니메이션과 트랜지션을 줄입니다.">
          <Toggle value={reduceMotion} onChange={setRM} />
        </Row>
      </Card>
    </>
  );
}

// ─── 5. Language / Region ─────────────────────────────────────────────────────
function SectionLang() {
  const { prefs, save } = usePrefs();
  const [lang, setLang]   = useState("ko");
  const [tz, setTz]       = useState("Asia/Seoul");
  const [df, setDf]       = useState("YYYY.MM.DD");
  const [tf, setTf]       = useState("24h");
  const [week, setWeek]   = useState("월요일");
  const [curr, setCurr]   = useState("KRW");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!prefs) return;
    setLang(prefs.language ?? "ko");
    setTz(prefs.timezone ?? "Asia/Seoul");
    setDf(prefs.date_format ?? "YYYY.MM.DD");
    setDirty(false);
  }, [prefs]);

  const mark = () => setDirty(true);
  const reset = () => {
    if (!prefs) return;
    setLang(prefs.language); setTz(prefs.timezone); setDf(prefs.date_format);
    setDirty(false);
  };
  const doSave = async () => {
    setSaving(true);
    await save({ language: lang, timezone: tz, date_format: df, week_start: week === "월요일" ? 1 : 0 });
    setSaving(false);
    setDirty(false);
  };

  return (
    <>
      <Card title="언어 / 지역">
        <Row label="표시 언어" bordered={false}>
          <Sel value={lang} onChange={(v) => { setLang(v); mark(); }} width={180}
            options={[
              { value: "ko", label: "한국어" },
              { value: "en", label: "English" },
              { value: "ja", label: "日本語" },
              { value: "zh", label: "中文 (간체)" },
            ]}
          />
        </Row>
        <Row label="시간대" hint={`현재 시각: ${new Date().toLocaleTimeString("ko-KR")} (KST · UTC+9)`}>
          <Sel value={tz} onChange={(v) => { setTz(v); mark(); }} width={220}
            options={["Asia/Seoul", "Asia/Tokyo", "Asia/Singapore", "America/Los_Angeles", "America/New_York", "Europe/London"]}
          />
        </Row>
      </Card>

      <Card title="날짜 / 시간 형식">
        <Row label="날짜" bordered={false}>
          <Sel value={df} onChange={(v) => { setDf(v); mark(); }} width={200}
            options={["YYYY.MM.DD", "YYYY-MM-DD", "MM/DD/YYYY", "DD/MM/YYYY", "YYYY년 M월 D일"]}
          />
        </Row>
        <Row label="시간">
          <Sel value={tf} onChange={setTf} width={200}
            options={[
              { value: "24h", label: "24시간 (14:32)" },
              { value: "12h", label: "12시간 (2:32 PM)" },
            ]}
          />
        </Row>
        <Row label="주 시작 요일">
          <Sel value={week} onChange={setWeek} width={180} options={["일요일", "월요일"]} />
        </Row>
      </Card>

      <Card title="숫자 / 통화">
        <Row label="기본 통화" bordered={false}>
          <Sel value={curr} onChange={setCurr} width={180}
            options={[
              { value: "KRW", label: "원 (₩)" },
              { value: "USD", label: "달러 ($)" },
              { value: "EUR", label: "유로 (€)" },
              { value: "JPY", label: "엔 (¥)" },
            ]}
          />
        </Row>
        <Row label="천 단위 구분" hint="1,234,567 vs 1234567">
          <Sel value="comma" onChange={() => {}} width={180}
            options={[
              { value: "comma", label: "쉼표 (1,234)" },
              { value: "none",  label: "없음 (1234)" },
              { value: "space", label: "공백 (1 234)" },
            ]}
          />
        </Row>
      </Card>

      {dirty && <SaveBar onCancel={reset} onSave={doSave} saving={saving} />}
    </>
  );
}

// ─── 6. Work Hours ────────────────────────────────────────────────────────────
function SectionWork() {
  const { prefs, save } = usePrefs();
  const DAYS = ["월", "화", "수", "목", "금", "토", "일"];
  const DAY_KEYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const [activeDays, setActiveDays] = useState([true, true, true, true, true, false, false]);
  const [start, setStart]           = useState("09:00");
  const [end, setEnd]               = useState("18:00");
  const [breakMin, setBreakMin]     = useState("60");
  const [autoCheck, setAutoCheck]   = useState(true);
  const [office, setOffice]         = useState("서울 본사");
  const [calBlock, setCalBlock]     = useState(true);
  const [dirty, setDirty]           = useState(false);
  const [saving, setSaving]         = useState(false);

  useEffect(() => {
    if (!prefs?.work_schedule) return;
    try {
      const ws = JSON.parse(prefs.work_schedule);
      if (ws.days) setActiveDays(DAY_KEYS.map((k) => ws.days.includes(k)));
      if (ws.start) setStart(ws.start);
      if (ws.end)   setEnd(ws.end);
      setDirty(false);
    } catch {}
  }, [prefs]);

  const mark = () => setDirty(true);
  const reset = () => {
    if (!prefs?.work_schedule) return;
    try {
      const ws = JSON.parse(prefs.work_schedule);
      if (ws.days) setActiveDays(DAY_KEYS.map((k) => ws.days.includes(k)));
    } catch {}
    setDirty(false);
  };
  const doSave = async () => {
    setSaving(true);
    const days = DAY_KEYS.filter((_, i) => activeDays[i]);
    await save({ work_schedule: JSON.stringify({ days, start, end, break_min: parseInt(breakMin) }) });
    setSaving(false);
    setDirty(false);
  };

  return (
    <>
      <Card title="근무 시간" desc="HR 시스템에 기록되는 표준 근무 시간입니다.">
        <Row label="근무 요일" bordered={false}>
          <div style={{ display: "flex", gap: 4 }}>
            {DAYS.map((d, i) => (
              <button
                key={d}
                onClick={() => { setActiveDays((prev) => prev.map((v, j) => j === i ? !v : v)); mark(); }}
                style={{
                  width: 32, height: 32, border: "1px solid",
                  borderColor: activeDays[i] ? "var(--p-600)" : "var(--border-strong)",
                  background: activeDays[i] ? "var(--p-600)" : "var(--surface)",
                  color: activeDays[i] ? "white" : (i >= 5 ? "var(--text-3)" : "var(--text-2)"),
                  borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                }}
              >{d}</button>
            ))}
          </div>
        </Row>
        <Row label="시작 시간">
          <Inp value={start} onChange={(v) => { setStart(v); mark(); }} width={90} mono />
        </Row>
        <Row label="종료 시간">
          <Inp value={end} onChange={(v) => { setEnd(v); mark(); }} width={90} mono />
        </Row>
        <Row label="휴게 시간 (분)" hint="점심 시간 등 비근무 시간">
          <Inp value={breakMin} onChange={(v) => { setBreakMin(v); mark(); }} width={70} mono />
        </Row>
      </Card>

      <Card title="자동화">
        <Row label="자동 출근 체크" hint="사내 WiFi 접속 시 자동으로 출근 처리" bordered={false}>
          <Toggle value={autoCheck} onChange={(v) => { setAutoCheck(v); mark(); }} />
        </Row>
        <Row label="근무지" hint="GPS 기반 재택/사무실 자동 분류">
          <Sel value={office} onChange={(v) => { setOffice(v); mark(); }} options={["서울 본사", "부산 지사", "재택", "외근"]} width={160} />
        </Row>
        <Row label="근무 시간 외 일정 차단" hint="캘린더에서 18:00 이후 회의 예약 시 경고">
          <Toggle value={calBlock} onChange={(v) => { setCalBlock(v); mark(); }} />
        </Row>
      </Card>

      {dirty && <SaveBar onCancel={reset} onSave={doSave} saving={saving} />}
    </>
  );
}

// ─── 7. Mail Signature ────────────────────────────────────────────────────────
function SectionSign() {
  const user = useAuth((s) => s.user);
  const [enable, setEnable]         = useState(true);
  const [includeReply, setIR]       = useState(true);
  const [sig, setSig]               = useState("");
  const [autoReply, setAutoReply]   = useState(false);
  const [arStart, setArStart]       = useState("");
  const [arEnd, setArEnd]           = useState("");
  const [dirty, setDirty]           = useState(false);
  const [saving, setSaving]         = useState(false);

  useEffect(() => {
    api.get<{ body_text: string; auto_attach: boolean }>("/mail/signature/")
      .then((r) => {
        setSig(r.data.body_text ?? "");
        setEnable(r.data.auto_attach ?? true);
        setDirty(false);
      })
      .catch(() => {
        // No signature yet — prefill with sensible default
        setSig(`${user?.name ?? ""} | 직책
부서 · TIMPLE
T. 010-0000-0000
E. ${user?.username ?? ""}@timple.me`);
      });
  }, []);

  const mark = () => setDirty(true);
  const doSave = async () => {
    setSaving(true);
    try {
      await api.put("/mail/signature/", { body_text: sig, auto_attach: enable });
      toast.success("메일 서명이 저장되었습니다");
      setDirty(false);
    } catch (e: any) {
      toast.error("저장 실패", e?.response?.data?.error?.message);
    } finally {
      setSaving(false);
    }
  };
  const reset = () => { setDirty(false); };

  return (
    <>
      <Card title="메일 서명" desc="새 메일과 답장에 자동으로 추가됩니다.">
        <Row label="서명 사용" bordered={false}>
          <Toggle value={enable} onChange={(v) => { setEnable(v); mark(); }} />
        </Row>
        <Row label="답장 / 전달에도 포함">
          <Toggle value={includeReply} onChange={(v) => { setIR(v); mark(); }} />
        </Row>
        <div style={{ padding: 18, borderTop: "1px solid var(--border)" }}>
          <div style={{ fontSize: 13.5, fontWeight: 500, marginBottom: 8 }}>서명 내용</div>
          <textarea
            value={sig}
            onChange={(e) => { setSig(e.target.value); mark(); }}
            style={{
              width: "100%", minHeight: 140, padding: 12, boxSizing: "border-box",
              border: "1px solid var(--border-strong)", borderRadius: 6,
              background: "var(--surface)", color: "var(--text)",
              fontFamily: "JetBrains Mono, monospace", fontSize: 12.5, lineHeight: 1.7,
              outline: "none", resize: "vertical",
            }}
          />
        </div>
        {sig && (
          <div style={{ padding: 18, borderTop: "1px solid var(--border)", background: "var(--panel)" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>미리보기</div>
            <div style={{
              padding: 14, background: "var(--surface)", borderRadius: 6,
              fontSize: 12.5, color: "var(--text-2)", whiteSpace: "pre-wrap", lineHeight: 1.7,
              borderLeft: "3px solid var(--p-600)",
            }}>{sig}</div>
          </div>
        )}
      </Card>

      <Card title="자동 응답 (휴가 모드)" desc="휴가 중일 때 자동으로 답장합니다.">
        <Row label="자동 응답 활성화" bordered={false}>
          <Toggle value={autoReply} onChange={setAutoReply} />
        </Row>
        <Row label="시작일">
          <Inp value={arStart} onChange={setArStart} mono width={130} placeholder="YYYY.MM.DD" />
        </Row>
        <Row label="종료일">
          <Inp value={arEnd} onChange={setArEnd} mono width={130} placeholder="YYYY.MM.DD" />
        </Row>
      </Card>

      {dirty && <SaveBar onCancel={reset} onSave={doSave} saving={saving} />}
    </>
  );
}

// ─── 8. Shortcuts ─────────────────────────────────────────────────────────────
const SHORTCUT_GROUPS = [
  { title: "전역", items: [
    { keys: "⌘ K",        label: "명령 팔레트 / 검색 열기" },
    { keys: "⌘ /",        label: "단축키 도움말" },
    { keys: "⌘ B",        label: "사이드바 접기/펼치기" },
    { keys: "⌘ Shift D",  label: "다크 모드 토글" },
    { keys: "G H",        label: "대시보드로 이동" },
    { keys: "G M",        label: "메일로 이동" },
    { keys: "G A",        label: "결재로 이동" },
    { keys: "G P",        label: "프로젝트로 이동" },
  ]},
  { title: "메일", items: [
    { keys: "C",          label: "새 메일 작성" },
    { keys: "R",          label: "답장" },
    { keys: "Shift R",    label: "전체 답장" },
    { keys: "F",          label: "전달" },
    { keys: "E",          label: "보관" },
    { keys: "#",          label: "삭제" },
    { keys: "S",          label: "별표 토글" },
  ]},
  { title: "결재", items: [
    { keys: "A",          label: "승인" },
    { keys: "Shift R",    label: "반려" },
    { keys: "H",          label: "보류" },
    { keys: "N",          label: "새 결재 작성" },
  ]},
  { title: "에디터", items: [
    { keys: "⌘ B",        label: "굵게" },
    { keys: "⌘ I",        label: "기울임" },
    { keys: "⌘ K",        label: "링크 삽입" },
    { keys: "⌘ S",        label: "저장 (자동저장 즉시)" },
  ]},
];

function Kbd({ keys }: { keys: string }) {
  return (
    <span style={{ display: "inline-flex", gap: 3 }}>
      {keys.split(" ").map((k, i) => (
        <span
          key={i}
          style={{
            padding: "2px 7px", minWidth: 22, textAlign: "center",
            background: "var(--panel)", border: "1px solid var(--border)",
            borderRadius: 4, fontSize: 11, fontWeight: 600,
            color: "var(--text-2)", boxShadow: "0 1px 0 var(--border)",
            fontFamily: "monospace",
          }}
        >{k}</span>
      ))}
    </span>
  );
}

function SectionKeys() {
  const [enabled, setEnabled]   = useState(true);
  const [seqKeys, setSeqKeys]   = useState(true);

  return (
    <>
      {SHORTCUT_GROUPS.map((g) => (
        <Card key={g.title} title={g.title}>
          {g.items.map((s, i) => (
            <Row key={i} label={s.label} bordered={i > 0}>
              <Kbd keys={s.keys} />
              <button className="t-iconbtn" style={{ width: 26, height: 26 }} title="변경">
                <I.Settings size={12} />
              </button>
            </Row>
          ))}
        </Card>
      ))}
      <Card title="설정">
        <Row label="단축키 사용" bordered={false}>
          <Toggle value={enabled} onChange={setEnabled} />
        </Row>
        <Row label="시퀀스 단축키 (G H 등)" hint="2단계 키 입력으로 빠르게 이동">
          <Toggle value={seqKeys} onChange={setSeqKeys} />
        </Row>
        <Row label="기본값으로 복원">
          <button className="t-btn t-btn-outline">초기화</button>
        </Row>
      </Card>
    </>
  );
}

// ─── 9. Data ──────────────────────────────────────────────────────────────────
function SectionData() {
  const user = useAuth((s) => s.user);
  const [exporting, setExporting]       = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const requestExport = async () => {
    setExporting(true);
    try {
      const r = await api.post<{ message: string }>("/users/me/export");
      toast.success("내보내기 요청됨", r.data.message);
    } catch (e: any) {
      toast.error("오류", e?.response?.data?.error?.message ?? "");
    } finally {
      setExporting(false);
    }
  };

  const resetUI = () => {
    try {
      ["timple-theme", "timple-modules", "timple-route",
       "timple-subnav-width", "timple-subnav-collapsed"].forEach((k) => localStorage.removeItem(k));
    } catch {}
    window.location.reload();
  };

  const STORAGE_ITEMS = [
    { color: "#2563EB", label: "메일",    value: "3.2 GB", pct: 32 },
    { color: "#7C3AED", label: "드라이브", value: "1.8 GB", pct: 18 },
    { color: "#16A34A", label: "첨부",    value: "0.8 GB", pct: 8 },
    { color: "#D97706", label: "기타",    value: "0.4 GB", pct: 4 },
  ];

  return (
    <>
      <Card title="내 데이터 내보내기" desc="GDPR / 개인정보보호법에 따른 데이터 이동성 권리를 행사할 수 있습니다.">
        <Row label="전체 데이터 다운로드" hint="ZIP 파일로 압축 · 요청 후 영업일 1일 이내 메일 발송" bordered={false}>
          <button className="t-btn t-btn-outline" onClick={requestExport} disabled={exporting}>
            {exporting ? "요청 중…" : "요청"}
          </button>
        </Row>
        <Row label="메일 내보내기 (mbox)" hint="받은편지함 + 보낸편지함">
          <button className="t-btn t-btn-outline">내보내기</button>
        </Row>
        <Row label="캘린더 내보내기 (.ics)" hint="과거 12개월 일정 포함">
          <button className="t-btn t-btn-outline">내보내기</button>
        </Row>
        <Row label="문서 내보내기 (ZIP)" hint="전자결재, 결재 문서 PDF + 첨부">
          <button className="t-btn t-btn-outline">내보내기</button>
        </Row>
      </Card>

      <Card title="가져오기">
        <Row label="이메일 가져오기 (.mbox · IMAP)" bordered={false}>
          <button className="t-btn t-btn-outline">가져오기</button>
        </Row>
        <Row label="연락처 (vCard · CSV)">
          <button className="t-btn t-btn-outline">가져오기</button>
        </Row>
        <Row label="외부 캘린더 (.ics · Google · Microsoft)">
          <button className="t-btn t-btn-outline">연결</button>
        </Row>
      </Card>

      <Card title="저장 공간">
        <div style={{ padding: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 8 }}>
            <span>사용량</span>
            <span style={{ fontFamily: "monospace" }}>
              <strong>6.2 GB</strong>
              <span style={{ color: "var(--text-3)" }}> / 10 GB</span>
            </span>
          </div>
          <div style={{ height: 10, background: "var(--panel)", borderRadius: 5, overflow: "hidden", display: "flex" }}>
            {STORAGE_ITEMS.map((s) => (
              <div key={s.label} style={{ width: `${s.pct}%`, background: s.color }} title={`${s.label} ${s.value}`} />
            ))}
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap", fontSize: 11.5 }}>
            {STORAGE_ITEMS.map((s) => (
              <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                <span style={{ color: "var(--text-2)" }}>{s.label}</span>
                <span style={{ fontWeight: 600, color: "var(--text-3)", fontFamily: "monospace" }}>{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card title="초기화 / 위험 영역">
        <Row label="UI 설정 초기화" hint="메뉴 순서, 서브메뉴, 너비, 라우트 등 UI 커스터마이징을 모두 기본값으로 되돌립니다." bordered={false}>
          {!confirmReset
            ? <button className="t-btn t-btn-outline" onClick={() => setConfirmReset(true)}>초기화</button>
            : (
              <div style={{ display: "flex", gap: 6 }}>
                <button className="t-btn" onClick={() => setConfirmReset(false)}>취소</button>
                <button
                  className="t-btn t-btn-primary"
                  onClick={resetUI}
                  style={{ background: "var(--danger)", borderColor: "var(--danger)" }}
                >정말로 초기화</button>
              </div>
            )}
        </Row>
        <Row label="계정 비활성화" hint="로그인할 수 없게 됩니다. 데이터는 보관됩니다.">
          {!confirmDeactivate
            ? <button className="t-btn" style={{ color: "var(--danger)" }} onClick={() => setConfirmDeactivate(true)}>비활성화</button>
            : (
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "var(--danger)" }}>정말요?</span>
                <button className="t-btn" onClick={() => setConfirmDeactivate(false)}>취소</button>
                <button className="t-btn" style={{ color: "var(--danger)", background: "color-mix(in srgb, var(--danger) 10%, transparent)" }}>
                  확인
                </button>
              </div>
            )}
        </Row>
        <Row label="계정 삭제" hint={`모든 데이터가 30일 후 영구 삭제됩니다. (${user?.email ?? ""})`}>
          <button
            className="t-btn t-btn-outline"
            style={{ color: "var(--danger)", borderColor: "color-mix(in srgb, var(--danger) 30%, transparent)" }}
          >삭제 요청</button>
        </Row>
      </Card>
    </>
  );
}
